import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, DatabaseZap, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'
import { lcOperationalResetAdminApi } from '@/api/lcOperationalReset'

interface Props {
  open: boolean
  organizationId: number
  centerName: string
  onOpenChange: (open: boolean) => void
}

const COUNT_LABELS: Record<string, string> = {
  accounts: '삭제할 계정',
  learningData: '학습·등록 데이터',
  classes: '클래스·시간표',
  payments: '결제·주문 내역',
  textbookRequests: '교재 요청',
  inquiries: '문의 내역',
  devices: '기기 관리',
  otherOperationalData: '기타 운영 데이터',
}

const PRESERVED_LABELS: Record<string, string> = {
  ORGANIZATION_CONFIGURATION: '기관 정보와 LC 운영 설정',
  PRODUCT_LICENSES_AND_CONTRACT: '상품 라이선스와 계약 설정',
  OWNER_ACCOUNT: '원장 계정과 멤버십',
  LC_ROLE_CONFIGURATION: 'LC 계정 역할 설정',
  SHARED_MEDIA_ACCESS: 'Superadmin 공유 자료 권한',
  RESET_AUDIT: '초기화 실행 감사 기록',
}

const BLOCKER_MESSAGES: Record<string, string> = {
  LC_OPERATIONAL_RESET_OWNER_REQUIRED: '원장 계정이 없어 초기화할 수 없습니다.',
  LC_OPERATIONAL_RESET_RUN_ACTIVE: '자동완료 작업이 진행 중입니다. 작업을 종료한 뒤 다시 시도하세요.',
  LC_OPERATIONAL_RESET_SHARED_LEARNER: '다른 tenant 또는 개인 상품에도 연결된 원생이 있습니다. 계정 소속을 먼저 정리하세요.',
  LC_OPERATIONAL_RESET_UNRESOLVED_OPERATION: '미확정 결제·환불·출고 작업이 있습니다. 운영 처리를 완료한 뒤 초기화하세요.',
}

export function canExecuteLcOperationalReset(
  centerName: string,
  confirmation: string,
  reason: string,
  blockerCount: number,
): boolean {
  return confirmation.trim() === centerName && reason.trim().length > 0 && blockerCount === 0
}

function apiErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return '센터 운영 데이터 초기화에 실패했습니다.'
  }
  const response = (error as {
    response?: { data?: { error?: { code?: string; message?: string } } }
  }).response
  const code = response?.data?.error?.code
  return (code && BLOCKER_MESSAGES[code]) || response?.data?.error?.message || '센터 운영 데이터 초기화에 실패했습니다.'
}

export function LcOperationalResetDialog({ open, organizationId, centerName, onOpenChange }: Props) {
  const queryClient = useQueryClient()
  const [confirmation, setConfirmation] = useState('')
  const [reason, setReason] = useState('')
  const preview = useQuery({
    queryKey: ['admin', 'organizations', organizationId, 'lc-operational-reset-preview'],
    queryFn: () => lcOperationalResetAdminApi.preview(organizationId),
    enabled: open,
  })
  const reset = useMutation({
    mutationFn: () => lcOperationalResetAdminApi.reset(
      organizationId,
      confirmation.trim(),
      reason.trim(),
    ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'members'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'test-completion-runs'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'lc-operational-reset-preview'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations', organizationId, 'lc-free-trial-program'] })
      toast.success(`${result.totalDeleted.toLocaleString()}건의 센터 운영 데이터를 초기화했습니다.`)
      onOpenChange(false)
    },
    onError: (error) => toast.error(apiErrorMessage(error)),
  })

  useEffect(() => {
    if (open) {
      setConfirmation('')
      setReason('')
    }
  }, [open])

  const blockers = preview.data?.blockers ?? []
  const canExecute = canExecuteLcOperationalReset(centerName, confirmation, reason, blockers.length)
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && reset.isPending) return
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>센터 운영 데이터 초기화</DialogTitle>
          <DialogDescription>
            상품과 기본 설정은 유지하고 센터를 신규 개설 직후의 운영 상태로 되돌립니다.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>복구할 수 없는 작업입니다</AlertTitle>
            <AlertDescription>
              원생·직원 계정, 학습 기록, 클래스, 결제, 교재 요청, 문의, 기기 데이터를 영구 삭제합니다.
              외부 결제나 출고가 미확정 상태이면 실행이 차단됩니다.
            </AlertDescription>
          </Alert>

          {preview.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Spinner />
              삭제 범위를 계산하는 중입니다.
            </div>
          ) : preview.isError ? (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>삭제 범위를 불러오지 못했습니다</AlertTitle>
              <AlertDescription>잠시 후 다시 시도하거나 backend 상태를 확인하세요.</AlertDescription>
            </Alert>
          ) : preview.data ? (
            <>
              <Card size="sm">
                <CardHeader>
                  <CardTitle>삭제 예정</CardTitle>
                  <CardDescription>총 {preview.data.total.toLocaleString()}건</CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {Object.entries(preview.data.counts).map(([key, count]) => (
                      <div key={key} className="flex flex-col gap-1 rounded-lg bg-muted p-3">
                        <dt className="text-xs text-muted-foreground">{COUNT_LABELS[key] ?? key}</dt>
                        <dd className="text-base font-semibold tabular-nums">{count.toLocaleString()}건</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>

              <Card size="sm">
                <CardHeader>
                  <CardTitle>유지되는 설정</CardTitle>
                  <CardDescription>초기화 후에도 다시 연결할 필요가 없습니다.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {preview.data.preserved.map((key) => (
                      <li key={key} className="flex items-center gap-2 text-sm">
                        <ShieldCheck className="text-primary" />
                        {PRESERVED_LABELS[key] ?? key}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {blockers.map((blocker) => (
                <Alert key={blocker.code} variant="destructive">
                  <AlertTriangle />
                  <AlertTitle>초기화할 수 없습니다</AlertTitle>
                  <AlertDescription>
                    {BLOCKER_MESSAGES[blocker.code] ?? blocker.code} ({blocker.count.toLocaleString()}건)
                  </AlertDescription>
                </Alert>
              ))}
            </>
          ) : null}

          <FieldGroup>
            <Field data-invalid={confirmation.length > 0 && confirmation.trim() !== centerName}>
              <FieldLabel htmlFor="lc-operational-reset-confirmation">
                확인을 위해 센터명 “{centerName}”을 입력하세요
              </FieldLabel>
              <Input
                id="lc-operational-reset-confirmation"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                aria-invalid={confirmation.length > 0 && confirmation.trim() !== centerName}
                autoComplete="off"
              />
              {confirmation.length > 0 && confirmation.trim() !== centerName ? (
                <FieldError>센터명이 일치하지 않습니다.</FieldError>
              ) : null}
            </Field>
            <Field>
              <FieldLabel htmlFor="lc-operational-reset-reason">초기화 사유</FieldLabel>
              <Textarea
                id="lc-operational-reset-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="예: 영업 시연 종료 후 교육용 센터 재설정"
              />
              <FieldDescription>실행자, 사유, 삭제 건수가 감사 기록으로 보존됩니다.</FieldDescription>
            </Field>
          </FieldGroup>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={reset.isPending}>
            취소
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => reset.mutate()}
            disabled={reset.isPending || preview.isLoading || preview.isError || !canExecute}
          >
            {reset.isPending ? <Spinner data-icon="inline-start" /> : <DatabaseZap data-icon="inline-start" />}
            {reset.isPending ? '초기화 중' : '운영 데이터 초기화'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
